import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { CircleDashed } from 'tabler-icons-react'
import { EnvelopeIcon } from '@heroicons/react/24/outline'

// Components
import { Modal } from '../Modal'
import { StateContainer } from '../StateContainer'

// Service
import { getUserEmailLogs, resendUserEmail } from '../../services/users.service'

// Utils
import colors from '../../utils/colors'
import { EMAIL_IDENTIFIER_LABELS } from '../../utils/constants'
import { toast } from '../../utils/helpers'

/**
 *
 * UserSentEmailsModal
 *
 * Displays the standardized emails sent to a user, with a resend action per entry.
 */
const UserSentEmailsModal = ({ setShowModal, showModal, user }) => {
  // State
  const [loadingLogs, setLoadingLogs] = useState(true)
  const [emailLogs, setEmailLogs] = useState([])
  const [loadingResend, setLoadingResend] = useState(null)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  const getUpdatedEmailLogs = async () => {
    const response = await getUserEmailLogs(user.id, handleErrors, setLoadingLogs)
    if (response) setEmailLogs(response)
  }

  useEffect(() => {
    getUpdatedEmailLogs()
  }, [])

  /**
   * Resends the email from the specified log entry and refreshes the history.
   * @param {object} log
   */
  const resendEmail = async (log) => {
    setLoadingResend(log.id)
    const response = await resendUserEmail(
      user.id,
      { identifier: log.identifier, eventExhibitor: log.eventExhibitor },
      handleErrors,
      () => {},
    )
    setLoadingResend(null)

    if (response) {
      handleSuccess(`${EMAIL_IDENTIFIER_LABELS[log.identifier] || log.identifier} email sent.`)
      getUpdatedEmailLogs()
    }
  }

  return (
    <Modal
      actions={[{ type: 'cancel', label: 'Close', onClick: () => setShowModal(false) }]}
      icon={<EnvelopeIcon className="h-5 stroke-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col gap-4 sm:mt-5">
          <span className="text-sm text-gray-600">
            Emails sent to{' '}
            <span className="font-bold">
              {user.firstName} {user.lastName}
            </span>{' '}
            ({user.email})
          </span>

          <StateContainer loading={loadingLogs}>
            {emailLogs.length === 0 ? (
              <span className="py-4 text-center text-sm text-gray-600">
                No emails have been sent to this user yet.
              </span>
            ) : (
              <ul className="max-h-80 divide-y divide-gray-200 overflow-y-auto">
                {emailLogs.map((log) => (
                  <li className="flex items-center justify-between gap-2 py-2" key={log.id}>
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-black">
                        {EMAIL_IDENTIFIER_LABELS[log.identifier] || log.identifier}
                      </span>
                      <span className="text-xs text-gray-600">
                        {log.eventName || log.exhibitorName
                          ? `${log.eventName || ''}${
                              log.eventName && log.exhibitorName ? ' — ' : ''
                            }${log.exhibitorName || ''}`
                          : null}
                      </span>
                      <span className="text-xs text-gray-600">
                        {log.sentAt
                          ? `Sent ${dayjs(log.sentAt).format('MMM D, YYYY h:mm A')}`
                          : `${log.status}...`}
                      </span>
                    </div>

                    {log.canResend && (
                      <button
                        className="px-2 py-0.5 font-bold text-purple hover:rounded-full hover:bg-status-purple disabled:opacity-50"
                        disabled={loadingResend !== null}
                        onClick={() => resendEmail(log)}
                        type="button"
                      >
                        <div className="flex flex-row items-center">
                          {loadingResend === log.id && (
                            <svg
                              className="mr-1 h-4 w-4 motion-safe:animate-spin-slow"
                              viewBox="0 0 24 24"
                            >
                              <CircleDashed
                                size={24}
                                strokeWidth={2}
                                color={colors.purple.DEFAULT}
                              />
                            </svg>
                          )}
                          Resend
                        </div>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </StateContainer>
        </div>
      }
      open={showModal}
      setOpen={setShowModal}
      title="Sent Emails"
    />
  )
}

UserSentEmailsModal.propTypes = {
  setShowModal: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
}

export default UserSentEmailsModal
